import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { toast } from 'react-toastify';
import styled from 'styled-components';

import Loading from '../../../components/Loading';
import { Container } from '../../../styles/layout/Container';
import {
   PageHeader,
   PageHeaderText,
   PageTitle,
   PageSubtitle,
   PageHeaderActions,
} from '../../../styles/layout/PageHeader';
import {
   Section,
   SectionBody,
   SectionHeader,
   SectionTitle,
} from '../../../styles/layout/Section';
import { FormGrid } from '../../../styles/layout/FormGrid';
import { Field } from '../../../styles/forms/Field';
import axios from '../../../services/axios';

// ─── Estilos locais ───────────────────────────────────────────────────────────

const ValidationStatusRow = styled.div`
   display: flex;
   align-items: flex-start;
   gap: 24px;
   flex-wrap: wrap;
`;

const ValidationCard = styled.div`
   flex: 1;
   min-width: 220px;
   border: 1px solid
      ${({ $isValidated, theme }) =>
         $isValidated
            ? theme?.colors?.success ?? 'rgba(46,125,50,0.4)'
            : theme?.colors?.border ?? 'rgba(0,0,0,0.1)'};
   border-radius: 8px;
   padding: 14px 16px;
   background: ${({ $isValidated }) =>
      $isValidated ? 'rgba(46,125,50,0.05)' : 'transparent'};
`;

const ValidationCardTitle = styled.p`
   font-size: 12px;
   font-weight: 600;
   color: ${({ theme }) => theme?.colors?.textMuted ?? '#666'};
   margin: 0 0 6px;
   text-transform: uppercase;
   letter-spacing: 0.04em;
`;

const ValidationCardStatus = styled.p`
   font-size: 13px;
   font-weight: 600;
   margin: 0 0 4px;
   color: ${({ $isValidated }) => ($isValidated ? '#2e7d32' : '#555')};
`;

const ValidationCardMeta = styled.p`
   font-size: 12px;
   color: ${({ theme }) => theme?.colors?.textMuted ?? '#888'};
   margin: 0;
`;

// ─── Modal de confirmação de exclusão ─────────────────────────────────────────
function DeleteConfirmationModal({ isOpen, isDeleting, onConfirm, onCancel }) {
   if (!isOpen) return null;
   return (
      <div
         style={{
            position: 'fixed',
            inset: 0,
            zIndex: 1000,
            background: 'rgba(0,0,0,0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
         }}
      >
         <div
            style={{
               background: '#fff',
               borderRadius: 12,
               padding: 32,
               maxWidth: 420,
               width: '90%',
               boxShadow: '0 8px 32px rgba(0,0,0,0.18)',
            }}
         >
            <h2 style={{ margin: '0 0 12px', fontSize: 18 }}>
               Excluir registro?
            </h2>
            <p style={{ margin: '0 0 24px', color: '#555', fontSize: 14 }}>
               Esta ação não pode ser desfeita. O registro será removido do
               sistema.
            </p>
            <div
               style={{ display: 'flex', gap: 12, justifyContent: 'flex-end' }}
            >
               <button type="button" onClick={onCancel} disabled={isDeleting}>
                  Cancelar
               </button>
               <button type="button" onClick={onConfirm} disabled={isDeleting}>
                  {isDeleting ? 'Excluindo...' : 'Confirmar Exclusão'}
               </button>
            </div>
         </div>
      </div>
   );
}

// ─── Modal de confirmação de validação ────────────────────────────────────────
function ValidationConfirmationModal({
   isOpen,
   validationType,
   isSubmitting,
   onConfirm,
   onCancel,
}) {
   const [notes, setNotes] = useState('');

   useEffect(() => {
      if (isOpen) setNotes('');
   }, [isOpen]);

   if (!isOpen) return null;

   const validationTypeLabel =
      validationType === 'FINANCE' ? 'Financeira' : 'Contábil';

   return (
      <div
         style={{
            position: 'fixed',
            inset: 0,
            zIndex: 1000,
            background: 'rgba(0,0,0,0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
         }}
      >
         <div
            style={{
               background: '#fff',
               borderRadius: 12,
               padding: 32,
               maxWidth: 460,
               width: '90%',
               boxShadow: '0 8px 32px rgba(0,0,0,0.18)',
            }}
         >
            <h2 style={{ margin: '0 0 8px', fontSize: 18 }}>
               Confirmar Validação {validationTypeLabel}
            </h2>
            <p
               style={{
                  margin: '0 0 20px',
                  color: '#d32f2f',
                  fontSize: 13,
                  fontWeight: 500,
               }}
            >
               ⚠️ Esta ação é irreversível. Após confirmada, não poderá ser
               desfeita.
            </p>
            <div style={{ marginBottom: 20 }}>
               <label
                  htmlFor="validation-notes"
                  style={{
                     display: 'block',
                     fontSize: 13,
                     marginBottom: 6,
                     color: '#444',
                  }}
               >
                  Observação (opcional)
               </label>
               <textarea
                  id="validation-notes"
                  rows={3}
                  placeholder="Ex: Protocolo nº 2026/0001, processado na competência 03/2026..."
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  disabled={isSubmitting}
                  style={{
                     width: '100%',
                     boxSizing: 'border-box',
                     padding: '8px 10px',
                     fontSize: 13,
                     borderRadius: 6,
                     border: '1px solid rgba(0,0,0,0.2)',
                     resize: 'vertical',
                  }}
               />
            </div>
            <div
               style={{ display: 'flex', gap: 12, justifyContent: 'flex-end' }}
            >
               <button type="button" onClick={onCancel} disabled={isSubmitting}>
                  Cancelar
               </button>
               <button
                  type="button"
                  onClick={() => onConfirm(notes)}
                  disabled={isSubmitting}
               >
                  {isSubmitting
                     ? 'Registrando...'
                     : `Confirmar Validação ${validationTypeLabel}`}
               </button>
            </div>
         </div>
      </div>
   );
}

// ─── Card de status de validação ──────────────────────────────────────────────
function ValidationStatusCard({
   title,
   validation,
   canValidate,
   onValidateClick,
}) {
   const isValidated = validation !== null && validation !== undefined;

   return (
      <ValidationCard $isValidated={isValidated}>
         <ValidationCardTitle>{title}</ValidationCardTitle>
         {isValidated ? (
            <>
               <ValidationCardStatus $isValidated>
                  ✓ Validado
               </ValidationCardStatus>
               <ValidationCardMeta>
                  {validation?.validated_at_label}
               </ValidationCardMeta>
               <ValidationCardMeta>
                  Por: {validation?.validated_by_user?.nome ?? '—'}
               </ValidationCardMeta>
               {validation?.notes && (
                  <ValidationCardMeta
                     style={{ marginTop: 6, fontStyle: 'italic' }}
                  >
                     "{validation.notes}"
                  </ValidationCardMeta>
               )}
            </>
         ) : (
            <>
               <ValidationCardStatus $isValidated={false}>
                  Pendente
               </ValidationCardStatus>
               {canValidate && (
                  <button
                     type="button"
                     onClick={onValidateClick}
                     style={{
                        marginTop: 10,
                        fontSize: 12,
                        padding: '4px 12px',
                     }}
                  >
                     Registrar Validação
                  </button>
               )}
            </>
         )}
      </ValidationCard>
   );
}

// ─── Helpers de formatação ────────────────────────────────────────────────────
function formatCurrency(value) {
   return Number(value).toLocaleString('pt-BR', {
      style: 'currency',
      currency: 'BRL',
   });
}

function formatDate(dateString) {
   if (!dateString) return '—';
   const [year, month, day] = dateString.split('-');
   return `${day}/${month}/${year}`;
}

function formatLegalEntity(legalEntity) {
   const labels = {
      MYSHOP_COMERCIO: 'MyShop Comércio',
      LIFE_PURIFICADORES: 'Life Purificadores',
   };
   return labels[legalEntity] ?? legalEntity;
}

function formatServiceCategory(serviceCategory) {
   const labels = {
      SOFTWARE: 'Software',
      ECOMMERCE: 'E-commerce',
      SERVICO_FISICO: 'Serviço Físico',
      CONSULTORIA: 'Consultoria',
      MARKETING_DIGITAL: 'Marketing Digital',
      OUTROS: 'Outros',
   };
   return labels[serviceCategory] ?? serviceCategory;
}

function formatProviderType(providerType) {
   const labels = {
      PJ: 'Pessoa Jurídica',
      PF: 'Pessoa Física',
      ESTRANGEIRO: 'Estrangeiro',
   };
   return labels[providerType] ?? providerType;
}

function formatDateTime(isoString) {
   if (!isoString) return '—';
   return new Date(isoString).toLocaleString('pt-BR');
}

// ─── Componente principal ─────────────────────────────────────────────────────
export default function ServiceAquisitionShow() {
   const { id } = useParams();
   const navigate = useNavigate();

   const userPermissions = useSelector(
      (state) => state.auth.user.permissions || []
   );
   const canValidateFinance = userPermissions.includes(
      'finance:service_aquisition_validation:create_financial'
   );
   const canValidateTax = userPermissions.includes(
      'tax:service_aquisition_validation:create_tax'
   );

   const [serviceAquisition, setServiceAquisition] = useState(null);
   const [isLoading, setIsLoading] = useState(true);
   const [isDeleting, setIsDeleting] = useState(false);
   const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
   const [validationModalType, setValidationModalType] = useState(null);
   const [isSubmittingValidation, setIsSubmittingValidation] = useState(false);

   const fetchServiceAquisition = useCallback(async () => {
      setIsLoading(true);
      try {
         const response = await axios.get(`/service-aquisition/${id}`);
         const fetchedRecord = response.data.data.serviceAquisition;
         const fetchedPresignedUrl = fetchedRecord.nfse.presigned_download_url;

         setServiceAquisition({
            ...fetchedRecord,
            nfse: {
               ...fetchedRecord.nfse,
               presigned_download_url: fetchedPresignedUrl,
            },
            validations: fetchedRecord.validations,
         });
      } catch (err) {
         const errorMessage =
            err.response?.data?.errors?.[0]?.message ??
            err.response?.data?.message ??
            'Erro ao carregar registro';
         toast.error(errorMessage);
         navigate('/finance/service-aquisition');
      } finally {
         setIsLoading(false);
      }
   }, [id, navigate]);

   useEffect(() => {
      fetchServiceAquisition();
   }, [fetchServiceAquisition]);

   async function handleDeleteConfirm() {
      setIsDeleting(true);
      try {
         await axios.delete(`/service-aquisition/${id}`);
         toast.success('Registro removido com sucesso');
         navigate('/finance/service-aquisition');
      } catch (err) {
         toast.error(err.response?.data?.message ?? 'Erro ao remover registro');
      } finally {
         setIsDeleting(false);
         setIsDeleteModalOpen(false);
      }
   }

   async function handleValidationConfirm(notes) {
      setIsSubmittingValidation(true);
      try {
         const response = await axios.post(
            `/service-aquisition/${id}/validations`,
            {
               validation_type: validationModalType,
               notes: notes?.trim() || undefined,
            }
         );
         toast.success(response.data.message);
         setValidationModalType(null);
         await fetchServiceAquisition();
      } catch (err) {
         toast.error(
            err.response?.data?.message ?? 'Erro ao registrar validação'
         );
      } finally {
         setIsSubmittingValidation(false);
      }
   }

   if (isLoading || !serviceAquisition) {
      return (
         <Container>
            <Loading isLoading />
         </Container>
      );
   }

   return (
      <Container>
         <Loading isLoading={isDeleting || isSubmittingValidation} />

         <DeleteConfirmationModal
            isOpen={isDeleteModalOpen}
            isDeleting={isDeleting}
            onConfirm={handleDeleteConfirm}
            onCancel={() => setIsDeleteModalOpen(false)}
         />

         <ValidationConfirmationModal
            isOpen={validationModalType !== null}
            validationType={validationModalType}
            isSubmitting={isSubmittingValidation}
            onConfirm={handleValidationConfirm}
            onCancel={() => setValidationModalType(null)}
         />

         <PageHeader>
            <PageHeaderText>
               <PageTitle>Serviço Tomado #{serviceAquisition.id}</PageTitle>
               <PageSubtitle>
                  {serviceAquisition.provider?.provider_name ?? '—'}
               </PageSubtitle>
            </PageHeaderText>
            <PageHeaderActions>
               <button
                  type="button"
                  onClick={() => navigate('/finance/service-aquisition')}
               >
                  Voltar
               </button>
               <button
                  type="button"
                  onClick={() =>
                     navigate(`/finance/service-aquisition/${id}/edit`)
                  }
               >
                  Editar
               </button>
               <button type="button" onClick={() => setIsDeleteModalOpen(true)}>
                  Excluir
               </button>
            </PageHeaderActions>
         </PageHeader>

         {/* ─── Dados do Serviço ─────────────────────────────────────────── */}
         <Section>
            <SectionHeader>
               <SectionTitle>Dados do Serviço</SectionTitle>
            </SectionHeader>
            <SectionBody>
               <FormGrid>
                  <Field className="col-4">
                     <label>Entidade Jurídica</label>
                     <p>{formatLegalEntity(serviceAquisition.legal_entity)}</p>
                  </Field>
                  <Field className="col-4">
                     <label>Categoria</label>
                     <p>
                        {formatServiceCategory(
                           serviceAquisition.service_category
                        )}
                     </p>
                  </Field>
                  <Field className="col-4">
                     <label>Valor Total</label>
                     <p>{formatCurrency(serviceAquisition.total_amount)}</p>
                  </Field>
                  <Field className="col-4">
                     <label>Período de Referência</label>
                     <p>{serviceAquisition.reference_period}</p>
                  </Field>
                  <Field className="col-4">
                     <label>Data de Emissão</label>
                     <p>
                        {formatDate(serviceAquisition.document_generation_date)}
                     </p>
                  </Field>
                  <Field className="col-12">
                     <label>Descrição do Serviço</label>
                     <p>{serviceAquisition.service_description ?? '—'}</p>
                  </Field>
               </FormGrid>
            </SectionBody>
         </Section>

         {/* ─── Prestador ────────────────────────────────────────────────── */}
         <Section>
            <SectionHeader>
               <SectionTitle>Prestador de Serviço</SectionTitle>
            </SectionHeader>
            <SectionBody>
               <FormGrid>
                  <Field className="col-6">
                     <label>Nome</label>
                     <p>{serviceAquisition.provider?.provider_name ?? '—'}</p>
                  </Field>
                  <Field className="col-3">
                     <label>Tipo</label>
                     <p>
                        {formatProviderType(
                           serviceAquisition.provider?.provider_type
                        )}
                     </p>
                  </Field>
                  <Field className="col-3">
                     <label>CNPJ</label>
                     <p>{serviceAquisition.provider?.cnpj_number ?? '—'}</p>
                  </Field>
               </FormGrid>
            </SectionBody>
         </Section>

         {/* ─── NFS-e + Validações ───────────────────────────────────────── */}
         <Section>
            <SectionHeader>
               <SectionTitle>NFS-e</SectionTitle>
            </SectionHeader>
            <SectionBody>
               <FormGrid>
                  <Field className="col-6">
                     <label>Número da NFS-e</label>
                     <p>{serviceAquisition.nfse.number ?? '—'}</p>
                  </Field>
                  <Field className="col-6">
                     <label>Arquivo PDF</label>
                     {serviceAquisition.nfse.has_file &&
                     serviceAquisition.nfse.presigned_download_url ? (
                        <a
                           href={serviceAquisition.nfse.presigned_download_url}
                           target="_blank"
                           rel="noopener noreferrer"
                        >
                           Visualizar / Baixar NFS-e
                        </a>
                     ) : (
                        <p>Nenhum arquivo anexado</p>
                     )}
                  </Field>
               </FormGrid>

               {/* ─── Subseção: Validações ────────────────────────────────── */}
               <div
                  style={{
                     marginTop: 20,
                     paddingTop: 16,
                     borderTop: '1px solid rgba(0,0,0,0.07)',
                  }}
               >
                  <p
                     style={{
                        fontSize: 13,
                        fontWeight: 600,
                        color: '#555',
                        margin: '0 0 12px',
                     }}
                  >
                     Validações do Documento
                  </p>
                  <ValidationStatusRow>
                     <ValidationStatusCard
                        title="Financeiro"
                        validation={serviceAquisition.validations.financial}
                        canValidate={canValidateFinance}
                        onValidateClick={() =>
                           setValidationModalType('FINANCE')
                        }
                     />
                     <ValidationStatusCard
                        title="Contabilidade"
                        validation={serviceAquisition.validations.tax}
                        canValidate={canValidateTax}
                        onValidateClick={() => setValidationModalType('TAX')}
                     />
                  </ValidationStatusRow>
               </div>
            </SectionBody>
         </Section>

         {/* ─── Metadados ────────────────────────────────────────────────── */}
         <Section>
            <SectionHeader>
               <SectionTitle>Informações do Registro</SectionTitle>
            </SectionHeader>
            <SectionBody>
               <FormGrid>
                  <Field className="col-4">
                     <label>Registrado por</label>
                     <p>{serviceAquisition.created_by_user?.nome ?? '—'}</p>
                  </Field>
                  <Field className="col-4">
                     <label>Criado em</label>
                     <p>
                        {formatDateTime(serviceAquisition.metadata.created_at)}
                     </p>
                  </Field>
                  <Field className="col-4">
                     <label>Última atualização</label>
                     <p>
                        {formatDateTime(serviceAquisition.metadata.updated_at)}
                     </p>
                  </Field>
               </FormGrid>
            </SectionBody>
         </Section>
      </Container>
   );
}
